import { useCallback, useEffect, useRef, useState } from "react";

import { getExternalProducts } from "services/menu/rest";
import { ExternalProductDto } from "services/menu/restModels";

import { useDebouncedValue } from "shared/hooks/useDebouncedValue";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export interface ExternalProductsState {
    products: ExternalProductDto[];
    hasMore: boolean;
    loading: boolean;
    query: string;
    setQuery: (query: string) => void;
    loadMore: () => void;
    // Каталог грузим по первому открытию селекта, а не на рендере вкладки: он нужен
    // только тем, кто правит привязку.
    ensureLoaded: () => void;
}

/*
* Каталог внешнего меню (iiko) для привязки позиции: поиск с постраничной подгрузкой.
* Кеша на уровне модуля нет намеренно — выборка зависит от запроса, а номенклатура меняется
* синхронизацией на соседней кнопке.
* */
export const useExternalProducts = (menuId: number): ExternalProductsState => {
    const [products, setProducts] = useState<ExternalProductDto[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [enabled, setEnabled] = useState<boolean>(false);
    const [query, setQuery] = useState<string>("");

    // Пустой запрос применяем сразу: после очистки поиска ждать 300 мс нечего.
    const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
    const appliedQuery = query.trim() === "" ? "" : debouncedQuery;

    // Счётчик выборки: ответ по прошлому запросу не должен дорисовать свои товары к новому.
    const scopeRef = useRef<number>(0);

    const ensureLoaded = useCallback(() => setEnabled(true), []);

    useEffect(() => {
        if (!enabled) return;

        scopeRef.current += 1;
        const scope = scopeRef.current;

        setLoading(true);

        getExternalProducts({ menuId, query: appliedQuery, skip: 0, limit: PAGE_SIZE })
            .then((page) => {
                if (scope !== scopeRef.current) return;

                setProducts(page?.items ?? []);
                setTotal(page?.total ?? 0);
            })
            .catch(() => {
                if (scope !== scopeRef.current) return;

                setProducts([]);
                setTotal(0);
            })
            .finally(() => {
                if (scope !== scopeRef.current) return;

                setLoading(false);
            });
    }, [menuId, appliedQuery, enabled]);

    const loadMore = useCallback(() => {
        if (loading) return;

        const scope = scopeRef.current;

        setLoading(true);

        getExternalProducts({
            menuId,
            query: appliedQuery,
            skip: products.length,
            limit: PAGE_SIZE,
        })
            .then((page) => {
                if (scope !== scopeRef.current) return;

                const items = page?.items ?? [];

                // Пустая страница при total больше загруженного: листать дальше нечего.
                // Без этого наблюдатель прокрутки звал бы догрузку по кругу.
                if (items.length === 0) {
                    setTotal(products.length);

                    return;
                }

                setProducts((current) => [...current, ...items]);
                setTotal(page?.total ?? 0);
            })
            .catch(() => undefined)
            .finally(() => {
                if (scope !== scopeRef.current) return;

                setLoading(false);
            });
    }, [menuId, appliedQuery, products.length, loading]);

    return {
        products,
        hasMore: products.length < total,
        loading,
        query,
        setQuery,
        loadMore,
        ensureLoaded,
    };
};
